import { Component, ReactNode } from "react";
import ReactDOM from "react-dom";
import { getElementId } from "../../hooks/use-element-id";
import { safelyAddClassName, safelyRemoveClassName } from "../../utils/dom-utils";

export interface PortalProps {
  /**
   * A React node to be mounted within the `Portal`.
   */
  children: ReactNode;

  /**
   * Life-cycle callback function that's called when the children have been mounted in to the Portal.
   */
  childrenDidMount?: () => void;

  /**
   * The target HTML element that children will be mounted in to.
   * If not passed, then the `Portal` will mount the children at the end of the `<body>` of the current HTML document.
   *
   * @default document.body
   */
  container?: HTMLElement;

  /**
   * List of space separated CSS classNames to be applied to container root element in to which the children are mounted.
   * Use it, for example, to position the target container at a desired location where you want the portaled content to
   * show up.
   */
  className?: string;
}

export interface PortalState {
  childrenMounted: boolean;
}

/**
 * The `Portal` component is a low-level utility component that's essentially an abstraction on top of the native React
 * portal feature.
 * @see https://reactjs.org/docs/portals.html
 *
 * This component mounts it's children in to a DOM node that can be outside the React node tree, while still preserving
 * it relationship and context with the parent React node.
 *
 * This is useful when we want to render components that visually overlay on top of other components,
 * e.g. Popovers, Dialogs, Tooltips etc. without worrying about special z-index handling.
 * Any class names passed to this element will be propagated to the new container element on document.body.
 *
 * @todo [BDS-531] add feature to look for portal root in `AppContext`
 * container usage precedence: container prop -> portalContainer from AppContext -> document.body (SSR Safe)
 */
export class Portal extends Component<PortalProps, PortalState> {
  private id: string = getElementId(undefined, "portal");

  private portalElement: HTMLElement = null;

  constructor(props: PortalProps) {
    super(props);
    this.state = {
      childrenMounted: false,
    };
  }

  public componentDidMount() {
    this.setState({ childrenMounted: true });
    this.portalElement = document.createElement("div");
    this.portalElement.setAttribute("id", this.id);
    safelyAddClassName(this.portalElement.classList, this.props.className);
    (this.props.container || document.body).appendChild(this.portalElement);
    this.props.childrenDidMount?.();
  }

  public componentDidUpdate(prevProps: PortalProps) {
    if (this.portalElement && prevProps.className !== this.props.className) {
      safelyRemoveClassName(this.portalElement.classList, prevProps.className);
      safelyAddClassName(this.portalElement.classList, this.props.className);
    }
  }

  public componentWillUnmount() {
    this.portalElement && this.portalElement.remove();
  }

  render() {
    if (this.state.childrenMounted && this.portalElement) {
      return ReactDOM.createPortal(this.props.children, this.portalElement);
    } else {
      return null;
    }
  }
}
